import { useEffect, useMemo, useRef, useState } from "react";

interface Plan {
  plan_id: string;
  available_credits: number;
  maximum_credits: number;
  subscription_expiry: Date | string;
  subscription_start_date: Date | string;
  is_free_trial: boolean;
}

interface CreditsPopupProps {
  fetchData: () => void;
  data: {
    all_plans?: Plan[];
    list_id?: string;
  };
}

const CreditsPopup = ({ fetchData, data }: CreditsPopupProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const landedCostCredit = useMemo(() => {
    if (!data?.all_plans?.length) return { current: 0, total: 0, days_left: 0, progress: 0, is_free_trial: false };
    const tempLcData = data.all_plans.find((credit) => credit.plan_id === "trademo_LANDED_COST");

    if (!tempLcData) return { current: 0, total: 0, days_left: 0, progress: 0, is_free_trial: false };

    const expiryDate = tempLcData.subscription_expiry instanceof Date ? tempLcData.subscription_expiry : new Date(tempLcData.subscription_expiry);
    const currentDate = new Date();
    const timeDiff = expiryDate.getTime() - currentDate.getTime();
    const daysLeft = Math.max(0, Math.ceil(timeDiff / (1000 * 60 * 60 * 24)));

    const progress = ((tempLcData.maximum_credits - tempLcData.available_credits) / tempLcData.maximum_credits) * 100;
    return {
      current: tempLcData.available_credits / 4,
      total: tempLcData.maximum_credits / 4,
      days_left: daysLeft,
      progress,
      is_free_trial: tempLcData.is_free_trial,
    };
  }, [data]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node) && buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!landedCostCredit.is_free_trial) return null;

  return (
    <div>
      <div className="tw-relative tw-flex tw-items-center tw-gap-2">
        <div className="tw-font-medium tw-bg-orange-500 tw-text-white tw-rounded-md tw-px-2 tw-py-1 tw-text-label-md">{landedCostCredit.days_left} day(s) free trial left</div>
        <button
          ref={buttonRef}
          onClick={() => {
            setIsOpen(!isOpen);
            if (!isOpen) fetchData();
          }}
          className="tw-border tw-rounded-3xl tw-shadow-sm tw-font-medium tw-flex tw-items-center tw-bg-white tw-px-2 tw-py-1 tw-w-fit tw-gap-1">
          <span className="tw-text-label-md">Credits Usage</span>
        </button>

        {isOpen && (
          <div
            ref={popupRef}
            className="tw-text-label-md tw-absolute tw-right-0 tw-top-8 tw-mt-2 tw-bg-white tw-rounded-lg tw-shadow-lg tw-border tw-border-gray-200 tw-z-10 tw-w-[300px]">
            <div className="tw-max-h-96 tw-overflow-y-auto tw-px-3">
              <div className="tw-border-gray-200 tw-p-1 tw-py-3">
                <div className="tw-h-2 tw-w-full tw-bg-gray-200 tw-rounded-full tw-overflow-hidden">
                  <div className="tw-h-full tw-bg-primary-purple-600 tw-transition-all tw-duration-500 tw-ease-in-out" style={{ width: `${landedCostCredit.progress}%` }} />
                </div>
                <div className="tw-mt-2 tw-text-sm tw-text-gray-600 tw-font-medium">
                  {landedCostCredit.current} / {landedCostCredit.total} transactions used
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditsPopup;
